<template>
  <div v-cloak>
    <p><strong>Attached Files</strong></p>
    <ul v-if="files.length > 0" class="list-group mb-4">
      <li v-for="file in files" class="list-group-item">
        <button v-on:click="removeFile(file.id)" type="button" class="mr-2 btn btn-sm btn-danger"><i
            class="fa fa-trash"></i></button>
        <template v-if="isLocal">
          <span>{{ file.filename }}</span>
          <span v-if="file.status" class="badge badge-secondary ml-2">{{ file.status }}</span>
          <span v-if="file.total_chunks" class="badge badge-info ml-2">{{ file.total_chunks }} chunks</span>
          <span v-if="file.total_tokens" class="badge badge-success ml-2">{{ file.total_tokens }} tokens</span>
          <span v-if="file.chunking_strategy" class="badge badge-primary ml-2">{{ file.chunking_strategy }}</span>
        </template>
        <template v-else>
          <a :href="`https://platform.openai.com/storage/files/${file.id}`" target="_blank">{{ file.filename }}</a>
        </template>
      </li>
    </ul>
    <p v-else class="alert alert-info">No files attached</p>
    <div v-if="errors.general" class="alert alert-danger">
      <i class="fa fa-exclamation-circle"></i>
      <strong>Error:</strong> {{ errors.general }}
    </div>
    <div class="alert alert-warning">
      <i class="fa fa-exclamation-triangle"></i>
      <strong>Warning:</strong>
      These fields must be set before selecting each file.
    </div>
    <div class="form-group" :class="{'has-error': errors.maxChunkSize}">
      <label :for="chunkSizeFieldName" class="control-label">
        {{ chunkSizeLabel }}:
        <small class="text-muted">({{ chunkLimits.min }}-{{ chunkLimits.max }})</small>
      </label>
      <input type="number" :class="['form-control', {'is-invalid': errors.maxChunkSize}]" :name="chunkSizeFieldName"
             value="800" :min="chunkLimits.min" :max="chunkLimits.max" v-model="maxChunkSize">
      <small v-if="errors.maxChunkSize" class="form-text text-danger">{{ errors.maxChunkSize }}</small>
    </div>
    <div class="form-group" :class="{'has-error': errors.chunkOverlap}">
      <label :for="chunkOverlapFieldName" class="control-label">
        {{ chunkOverlapLabel }}:
        <small class="text-muted">({{ chunkLimits.overlapMin }}-{{ chunkLimits.overlapMax }})</small>
      </label>
      <input type="number" :class="['form-control', {'is-invalid': errors.chunkOverlap}]" :name="chunkOverlapFieldName"
             value="400" :min="chunkLimits.overlapMin" :max="chunkLimits.overlapMax" v-model="chunkOverlap">
      <small v-if="errors.chunkOverlap" class="form-text text-danger">{{ errors.chunkOverlap }}</small>
    </div>
    <div v-if="isLocal" class="form-group">
      <label for="chunking_strategy" class="control-label">Chunking Strategy:</label>
      <select id="chunking_strategy" class="form-control" v-model="chunkingStrategy">
        <option value="fixed">Fixed - Split into fixed-size chunks (faster, less context)</option>
        <option value="semantic">Semantic - Split by paragraphs and sentences (preserves context)</option>
      </select>
    </div>
    <file-pond
        ref="pond"
        :server="pondServer"
        accepted-file-types="application/pdf, text/plain, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        label-idle="Drop files here or <span class='filepond--label-action'>Browse</span>"
        name="file"
        v-bind:allow-multiple="false"
        @processfile="handleFileSuccess"
        @processfileerror="handleFileFailure"
    />
  </div>
</template>

<script>
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import Logger from '../../common/Logger'

const FilePond = vueFilePond(FilePondPluginFileValidateType)

export default {
  props: {
    vector: {
      type: [String, Number],
      required: true
    },
    filesp: {
      type: String,
      required: true
    },
    isLocal: {
      type: Boolean,
      default: false
    }
  },
  components: {
    FilePond
  },
  data() {
    return {
      files: [],
      maxChunkSize: 800,
      chunkOverlap: 400,
      chunkingStrategy: 'fixed',
      errors: {
        maxChunkSize: null,
        chunkOverlap: null,
        general: null
      }
    }
  },
  computed: {
    chunkLimits() {
      return this.isLocal
        ? { min: 100, max: 2000, overlapMin: 0, overlapMax: 1000 }
        : { min: 100, max: 4096, overlapMin: 100, overlapMax: 4096 }
    },

    chunkSizeLabel() {
      return this.isLocal ? 'Max Chunk Size (characters)' : 'Max Chunk Size (tokens)'
    },

    chunkOverlapLabel() {
      return this.isLocal ? 'Chunk Overlap (characters)' : 'Chunk Overlap (tokens)'
    },

    chunkSizeFieldName() {
      return this.isLocal ? 'max_chunk_size' : 'max_chunk_size_tokens'
    },

    chunkOverlapFieldName() {
      return this.isLocal ? 'chunk_overlap' : 'chunk_overlap_tokens'
    },

    uploadUrl() {
      return this.isLocal
        ? `/api/vector-stores/${this.vector}/files`
        : `/vector-stores/${this.vector}/upload`
    },

    pondServer() {
      return {
        process: {
          url: this.uploadUrl,
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            'Accept': 'application/json',
          },
          ondata: (formData) => {
            if (!this.validateChunkSettings()) {
              throw new Error(this.errors.maxChunkSize || this.errors.chunkOverlap || 'Validation failed')
            }

            if (this.isLocal) {
              formData.append('max_chunk_size', this.maxChunkSize)
              formData.append('chunk_overlap', this.chunkOverlap)
              formData.append('chunking_strategy', this.chunkingStrategy)
            } else {
              formData.append('max_chunk_size_tokens', this.maxChunkSize)
              formData.append('chunk_overlap_tokens', this.chunkOverlap)
            }

            return formData;
          }
        },
      }
    }
  },
  methods: {
    validateChunkSettings() {
      this.errors.maxChunkSize = null
      this.errors.chunkOverlap = null
      this.errors.general = null

      const limits = this.chunkLimits
      const unit = this.isLocal ? 'characters' : 'tokens'

      if (this.maxChunkSize < limits.min || this.maxChunkSize > limits.max) {
        this.errors.maxChunkSize = `Max chunk size must be between ${limits.min} and ${limits.max} ${unit}`
        return false
      }

      if (this.chunkOverlap < limits.overlapMin || this.chunkOverlap > limits.overlapMax) {
        this.errors.chunkOverlap = `Chunk overlap must be between ${limits.overlapMin} and ${limits.overlapMax} ${unit}`
        return false
      }

      if (this.chunkOverlap > this.maxChunkSize / 2) {
        this.errors.chunkOverlap = `Chunk overlap cannot be greater than half of max chunk size (${Math.floor(this.maxChunkSize / 2)} ${unit})`
        return false
      }

      return true
    },

    handleFileSuccess(error, response) {
      if (error) {
        this.errors.general = error.message || 'Failed to upload file'
        Logger.error(error, { context: 'handleFileSuccess - upload error' })
        return
      }

      this.errors.general = null

      try {
        let resp = JSON.parse(response.serverId)

        if (this.isLocal) {
          this.files.push({
            id: resp.id,
            filename: resp.filename || resp.id,
            status: resp.status || 'pending',
            total_chunks: resp.total_chunks || 0,
            total_tokens: resp.total_tokens || null,
            chunking_strategy: resp.chunking_strategy || this.chunkingStrategy
          })
        } else {
          this.files.push({
            id: resp.id,
            filename: resp.id
          })
        }
      } catch (e) {
        this.errors.general = 'Failed to process server response'

        Logger.error(e, {
          response: response.serverId,
          context: 'handleFileSuccess - parsing error'
        })

        throw e
      }
    },
    handleFileFailure(error, file) {
      if (error && error.body) {
        try {
          const errorData = typeof error.body === 'string' ? JSON.parse(error.body) : error.body

          if (errorData.errors) {
            if (errorData.errors.max_chunk_size) {
              this.errors.maxChunkSize = errorData.errors.max_chunk_size[0]
            }
            if (errorData.errors.chunk_overlap) {
              this.errors.chunkOverlap = errorData.errors.chunk_overlap[0]
            }
            if (errorData.errors.file) {
              this.errors.general = errorData.errors.file[0]
            }
            if (errorData.message && !this.errors.general) {
              this.errors.general = errorData.message
            }
          } else if (errorData.error) {
            this.errors.general = errorData.error
          } else if (errorData.message) {
            this.errors.general = errorData.message
          } else {
            this.errors.general = 'Failed to upload file. Please try again.'
          }
        } catch (parseError) {
          this.errors.general = error.message || 'Failed to upload file. Please try again.'

          Logger.error(parseError, {
            originalError: error,
            errorBody: error.body,
            context: 'handleFileFailure - parsing error'
          })
        }
      } else {
        this.errors.general = error.message || 'Failed to upload file. Please try again.'
      }

      if (!this.errors.maxChunkSize && !this.errors.chunkOverlap) {
        Logger.error(error, {
          file: file ? file.name : 'unknown',
          errorMessage: this.errors.general,
          context: 'handleFileFailure - upload failed'
        })
      }
    },
    async removeFile(id) {
      try {
        const url = this.isLocal
          ? `/api/vector-stores/${this.vector}/files/${id}`
          : `/vector-stores/${this.vector}/file/${id}`

        const resp = await fetch(url, {
          method: 'DELETE',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          }
        })

        if (resp.status === 200 || resp.status === 204) {
          this.files = this.files.filter((item) => item.id !== id)
        } else {
          const errorText = await resp.text()
          this.errors.general = 'Failed to delete file. Please try again.'

          Logger.message('Failed to delete file', {
            fileId: id,
            status: resp.status,
            response: errorText,
            context: 'removeFile - delete failed'
          })
        }
      } catch (error) {
        this.errors.general = 'An error occurred while deleting the file.'

        Logger.error(error, {
          fileId: id,
          context: 'removeFile - exception'
        })
      }
    }
  },
  mounted() {
    this.files = JSON.parse(this.filesp)
  }
}
</script>
